export interface ChainScopedSymbolRouteTarget {
  readonly params: {
    readonly chainId: string;
    readonly symbol: string;
  };
  readonly to: "/symbol/$chainId/$symbol";
}

export interface CanonicalSymbolRouteTarget {
  readonly params: {
    readonly symbol: string;
  };
  readonly to: "/crypto/$symbol" | "/stocks/$symbol";
}

export interface UnifiedSymbolRouteTarget {
  readonly params: {
    readonly symbol: string;
  };
  readonly to: "/symbol/$symbol";
}

const SYMBOL_ROUTE_PARAM_PATTERN = /^[A-Z0-9.-]{1,16}$/u;
const SYMBOL_ROUTE_PATH_PARAM_PATTERN = /^[A-Za-z0-9.-]{1,16}$/u;

export function normalizeSymbolRouteParam(
  symbol: string | null | undefined,
): string | null {
  const normalized = symbol?.trim().toUpperCase();
  return normalized && SYMBOL_ROUTE_PARAM_PATTERN.test(normalized)
    ? normalized
    : null;
}

export function parseCanonicalSymbolRouteParams(rawParams: {
  "**"?: string;
  symbol: string;
}): { symbol: string } {
  if (rawParams["**"] !== undefined) {
    return false as never;
  }

  // A bare decimal segment (e.g. "/symbol/8453") is a chain id, not a
  // symbol. Keep the class-scoped parsers identical to the legacy bare route.
  if (normalizeSymbolRouteChainId(rawParams.symbol)) {
    return false as never;
  }

  const symbol = normalizeSymbolRouteParam(rawParams.symbol);
  return symbol ? { symbol } : (false as never);
}

export function normalizeSymbolRoutePathParam(
  symbol: string | null | undefined,
): string | null {
  const normalized = symbol?.trim();
  return normalized && SYMBOL_ROUTE_PATH_PARAM_PATTERN.test(normalized)
    ? normalized
    : null;
}

export function normalizeSymbolRouteChainId(
  chainId: string | null | undefined,
): string | null {
  const normalized = chainId?.trim();
  if (!normalized || !/^[1-9][0-9]*$/.test(normalized)) {
    return null;
  }

  const value = Number.parseInt(normalized, 10);
  return Number.isSafeInteger(value) && value > 0 ? String(value) : null;
}

export function parseSymbolRouteChainId(
  chainId: string | null | undefined,
): number | null {
  const normalized = normalizeSymbolRouteChainId(chainId);
  if (!normalized) {
    return null;
  }

  const value = Number.parseInt(normalized, 10);
  return Number.isSafeInteger(value) && value > 0 ? value : null;
}

export function buildUnifiedSymbolRouteTarget(
  symbol: string | null | undefined,
): UnifiedSymbolRouteTarget | null {
  const normalized = normalizeSymbolRouteParam(symbol);
  if (!normalized) {
    return null;
  }

  return {
    params: { symbol: normalized },
    to: "/symbol/$symbol",
  };
}

export function buildUnifiedSymbolHref(
  symbol: string | null | undefined,
): string | null {
  const target = buildUnifiedSymbolRouteTarget(symbol);
  return target ? `/symbol/${encodeURIComponent(target.params.symbol)}` : null;
}

export function buildChainScopedSymbolRouteTarget(input: {
  readonly chainId: number | string | null | undefined;
  readonly symbol: string | null | undefined;
}): ChainScopedSymbolRouteTarget | null {
  const chainId = normalizeSymbolRouteChainId(
    input.chainId === null || input.chainId === undefined
      ? null
      : String(input.chainId),
  );
  const symbol = normalizeSymbolRoutePathParam(input.symbol);
  if (!chainId || !symbol) {
    return null;
  }

  return {
    params: { chainId, symbol },
    to: "/symbol/$chainId/$symbol",
  };
}

export function buildChainScopedSymbolHref(input: {
  readonly chainId: number | string | null | undefined;
  readonly symbol: string | null | undefined;
}): string | null {
  const target = buildChainScopedSymbolRouteTarget(input);
  return target
    ? `/symbol/${target.params.chainId}/${encodeURIComponent(
        target.params.symbol,
      )}`
    : null;
}

const SYMBOL_ROUTE_ASSET_CLASSES = new Set(["crypto", "stock"]);

export type SymbolRouteAssetClass = "crypto" | "stock";

// Class→section registry (edge-seo ADR-001): the ONLY place an asset class
// maps to a public URL section. Future sections (/etfs, /bonds) are added
// here — plus 301s from the old section — never as scattered conditionals.
export const CANONICAL_SECTION_REGISTRY: Readonly<
  Record<
    SymbolRouteAssetClass,
    {
      readonly openGraphAssetClass: "crypto" | "tradfi";
      readonly section: string;
      readonly to: "/crypto/$symbol" | "/stocks/$symbol";
    }
  >
> = {
  crypto: {
    openGraphAssetClass: "crypto",
    section: "crypto",
    to: "/crypto/$symbol",
  },
  stock: {
    openGraphAssetClass: "tradfi",
    section: "stocks",
    to: "/stocks/$symbol",
  },
};

// Validates the optional ?class= disambiguation hint carried on canonical
// symbol links so callers (e.g. a crypto card) can steer resolution past a
// colliding stock ticker. Anything else is ignored.
export function normalizeSymbolRouteAssetClass(
  value: string | null | undefined,
): SymbolRouteAssetClass | null {
  const normalized = value?.trim().toLowerCase();
  return normalized && SYMBOL_ROUTE_ASSET_CLASSES.has(normalized)
    ? (normalized as SymbolRouteAssetClass)
    : null;
}

export function toSymbolRouteAssetClass(
  assetClass: string | null | undefined,
): SymbolRouteAssetClass | null {
  const normalized = assetClass?.trim().toLowerCase();
  if (normalized === "crypto") {
    return "crypto";
  }
  if (
    normalized === "stock" ||
    normalized === "tradfi" ||
    normalized === "equity" ||
    normalized === "fund"
  ) {
    return "stock";
  }
  return null;
}

export function buildCanonicalSymbolRouteTarget(
  symbol: string | null | undefined,
  assetClass: SymbolRouteAssetClass,
): CanonicalSymbolRouteTarget | null {
  const normalized = normalizeSymbolRouteParam(symbol);
  if (!normalized) {
    return null;
  }

  return {
    params: { symbol: normalized },
    to: CANONICAL_SECTION_REGISTRY[assetClass].to,
  };
}

export function buildCanonicalSymbolHref(
  symbol: string | null | undefined,
  assetClass: SymbolRouteAssetClass,
): string | null {
  const target = buildCanonicalSymbolRouteTarget(symbol, assetClass);
  if (!target) {
    return null;
  }

  const { section } = CANONICAL_SECTION_REGISTRY[assetClass];
  return `/${section}/${encodeURIComponent(target.params.symbol)}`;
}

// Compatibility name retained for existing callers; class is now expressed
// in the canonical path rather than a content-affecting query parameter.
export function buildCanonicalSymbolHrefWithClass(
  symbol: string | null | undefined,
  assetClass: SymbolRouteAssetClass,
): string | null {
  return buildCanonicalSymbolHref(symbol, assetClass);
}
